import moment from "moment";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { masks, UNIREG_MASK_KEY } from "../theme/default/masks";
import { FIELDS_WITHOUT_REQUIRED_VALIDATION, FIELD_PARAMS } from "./constants";
import { validateCard } from "./credit-card-validators";

// eslint-disable-next-line max-len
export const MAIN_PASSWORD_PATTERN =
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[-+=_@$!%*#?&^.,<>()])[A-Za-z\d+-=_@$!%*#?&^.,<>()]{9,}$/;

const EMAIL_PATTERN = new RegExp(
  "[a-zA-Z0-9\\+\\.\\_\\%\\-\\+]{1,256}" +
    "\\@" +
    "[a-zA-Z0-9][a-zA-Z0-9\\-]{0,64}" +
    "(" +
    "\\." +
    "[a-zA-Z0-9][a-zA-Z0-9\\-]{0,25}" +
    ")+"
);

const ZIP_CODE_PATTERN = /^[0-9]{5}(?:-[0-9]{4})?$/;

const MONTH_PATTERN = /(0[1-9]|10|11|12)/;

const YEAR_PATTERN = /20[0-9]{2}/;

const SHORT_YEAR_PATTERN = /[0-9]{2}/;

const NOT_NUMBERS_PATTERN = /\D+/g;

const ONLY_SPACES_PATTERN = /^\s*$/;

const validateIfNeeded = ({ field, value, settings }, regexp, paramName) => {
  const title = field.title || "";
  return (
    checkIfIsOptional(field, value) ||
    checkIfParamDoesNotExist(field, paramName) || {
      isValid: regexp.test(value),
      message: settings.translations.field_invalid.replace(
        "%field_name%",
        title
      )
    }
  );
};

const isMaskedNumbers = field =>
  field.params.includes("PhoneNumber") || field.params.includes("DateOfBirth");

const validateRegExp = (value, field) => {
  const result = (field.data?.regex_validation || [])
    .map(regexItem => {
      const isValid = new RegExp(regexItem.pattern).test(value);
      return {
        isValid: regexItem.type === "inverse_find" ? !isValid : isValid,
        message: regexItem.error
      };
    })
    .find(validator => !validator.isValid);
  return (
    result || {
      isValid: true,
      message: null
    }
  );
};

const checkIfParamDoesNotExist = (field, paramName) => {
  if (!field.params.split("|").includes(paramName)) {
    return {
      isValid: true,
      message: null
    };
  }
  return false;
};

const checkIfIsOptional = (field, value) => {
  const isOptional = field.params
    .split("|")
    .includes("TDFieldOptionIsOptional");
  if (isOptional && !value) {
    return {
      isValid: true,
      message: null
    };
  }
  return false;
};

const checkIfNeedToSkipRequired = field => {
  if (FIELDS_WITHOUT_REQUIRED_VALIDATION.includes(field.name)) {
    return {
      isValid: true,
      message: null
    };
  }
  return false;
};

const checkIfIsValidRequired = (field, value) => {
  if (isMaskedNumbers(field)) {
    value = value?.replace(NOT_NUMBERS_PATTERN, "");
  }
  return !!(
    value &&
    !value.match(ONLY_SPACES_PATTERN) &&
    value !== "currentLocation"
  );
};

const checkIfConfirmationMatch = (
  fieldsArray,
  confirmedName,
  confirmationValue
) => {
  const confirmedItem = fieldsArray.find(
    fieldItem => fieldItem.fieldName === confirmedName
  );

  if (!confirmedItem) return false;

  return confirmedItem.value ? confirmedItem.value === confirmationValue : true;
};

const validateRequiredIfNeeded = ({
  field,
  value,
  settings,
  isUnifiedRegistration
}) => {
  const title = field.title
    ? field.title.replace(/\*$/, "")
    : field.label
    ? field.label.replace(/\*$/, "")
    : "";
  const errorNameKey = isUnifiedRegistration
    ? "field_name_required"
    : "field_missing";
  if (field.params.split("|").includes("TDFieldIsCancelReason")) {
    return (
      checkIfIsOptional(field, value) || {
        isValid: checkIfIsValidRequired(field, value),
        message: settings.translations.reason_field_validation
      }
    );
  }
  if (field.params.split("|").includes("TDFieldIsCancelReasonOther")) {
    return (
      checkIfIsOptional(field, value) || {
        isValid: checkIfIsValidRequired(field, value),
        message: settings.translations.other_reason_field_validation
      }
    );
  }
  return (
    checkIfIsOptional(field, value) ||
    checkIfNeedToSkipRequired(field) || {
      isValid: checkIfIsValidRequired(field, value),
      message:
        field.data?.validation_error_string ||
        settings.translations[errorNameKey].replace("%field_name%", title)
    }
  );
};

const getPhoneError = ({ field, value, settings, isUnifiedRegistration }) => {
  const title = field.title || "";
  const mask = isUnifiedRegistration
    ? masks.PhoneNumber[UNIREG_MASK_KEY]
    : masks.PhoneNumber.default;
  let message = "";

  if (!mask.pattern.test(value)) {
    message = isUnifiedRegistration
      ? settings.translations.check_the_format
      : settings.translations.field_invalid.replace("%field_name%", title);
  }

  return {
    isValid: !message,
    message
  };
};

const validatePhoneNumberIfNeeded = ({
  field,
  value,
  settings,
  isUnifiedRegistration
}) => {
  return (
    checkIfIsOptional(field, value) ||
    checkIfParamDoesNotExist(field, "TDFieldOptionPhoneNumber") ||
    getPhoneError({
      field,
      value,
      settings,
      isUnifiedRegistration
    })
  );
};

const validateCountryPhoneNumberIfNeeded = ({ field, value, settings }) =>
  checkIfIsOptional(field, value) ||
  checkIfParamDoesNotExist(field, "TDFieldOptionCountryPhoneNumber") || {
    isValid: value ? isPossiblePhoneNumber(value) : false,
    message: settings.translations.field_invalid.replace(
      "%field_name%",
      field.title || ""
    )
  };

const validateModernizedPhoneNumber = ({ field, value, settings }) => {
  if (
    field.type === "modernizedPhoneNumber" &&
    !checkIfIsOptional(field, value)
  ) {
    return {
      isValid: value ? isPossiblePhoneNumber(value) : false,
      message: settings.translations.field_invalid.replace(
        "%field_name%",
        field.label || ""
      )
    };
  } else {
    return {
      isValid: true,
      message: null
    };
  }
};

const validateRegExpIfNeeded = ({ field, value }) =>
  checkIfIsOptional(field, value) ||
  checkIfParamDoesNotExist(field, "TDFieldOptionRegexValidation") ||
  validateRegExp(value, field);

const getDOBError = ({
  field,
  value,
  settings,
  brandCountryCode,
  isUnifiedRegistration
}) => {
  const title = field.title || "";
  const locale =
    field?.data?.locale || (isUnifiedRegistration && brandCountryCode) || null;
  const mask = masks.DateOfBirth[locale] || masks.DateOfBirth.default;
  const dobFormated = moment(value, mask.placeholder);
  let message = "";

  if (!mask.pattern.test(value) || !dobFormated.isValid()) {
    message = settings.translations.field_not_valid.replace(
      "%field_name%",
      title
    );
  } else if (
    dobFormated.isAfter(moment.now()) &&
    title !== "When was your last visit to the doctor? *"
  ) {
    message = settings.translations.dob_future_date_error;
  } else if (
    dobFormated.isAfter(moment.now()) &&
    // In future DOB and last visit date validation should be separated
    title === "When was your last visit to the doctor? *"
  ) {
    message = settings.translations.last_visit_future_date_error;
  } else if (
    isUnifiedRegistration &&
    moment().diff(moment(dobFormated), "years") < 13
  ) {
    message = settings.translations.dob_min_age_error;
  } else if (
    isUnifiedRegistration &&
    moment().diff(moment(dobFormated), "years") < 18
  ) {
    message = settings.translations.dob_limitation_warning;
  }

  return {
    isValid: !message,
    message
  };
};

const validateDOBIfNeeded = ({
  field,
  value,
  settings,
  brandCountryCode,
  isUnifiedRegistration
}) => {
  return (
    checkIfIsOptional(field, value) ||
    checkIfParamDoesNotExist(field, "TDFieldOptionDateOfBirth") ||
    getDOBError({
      field,
      value,
      settings,
      brandCountryCode,
      isUnifiedRegistration
    })
  );
};

const validateExpirationDateIfNeeded = ({
  field,
  value,
  fieldsArray,
  settings
}) => {
  const isOptionOrParamDoesNotExists = checkIfIsOptional(field, value);
  const isExpDate = checkIfParamDoesNotExist(
    field,
    "TDFieldOptionExpirationDate"
  );
  const isCardExpDate = checkIfParamDoesNotExist(
    field,
    "TDFieldOptionCreditCardExpDate"
  );
  if (isOptionOrParamDoesNotExists || (isExpDate && isCardExpDate)) {
    return isOptionOrParamDoesNotExists || (isExpDate && isCardExpDate);
  }

  const checkMonth = MONTH_PATTERN.test(value?.split("/")[0]);
  const checkYear = SHORT_YEAR_PATTERN.test(value?.split("/")[1]);
  if (
    !isCardExpDate &&
    !(
      checkMonth &&
      checkYear &&
      moment(value, "MM/YY").add(1, "months").isAfter(moment.now())
    )
  ) {
    return {
      isValid: false,
      message: settings.translations.card_expiration_invalid
    };
  }

  const invalidField = fieldsArray.find(
    _field => !_field.isValid && _field.fieldName !== "cc_info.exp_year"
  );
  const monthField = fieldsArray.find(
    _field => _field.fieldName === "cc_info.exp_month"
  );
  if (
    !invalidField &&
    monthField &&
    !moment(`${monthField.value}/${value}`, "MM/YYYY")
      .add(1, "months")
      .isAfter(moment.now())
  ) {
    return {
      isValid: false,
      message: settings.translations.card_expiration_invalid
    };
  } else {
    return {
      isValid: true,
      message: null
    };
  }
};

const validateCreditCardIfNeeded = ({ field, value, unmasks, settings }) => {
  const title = field.title || "";
  return (
    /* eslint-disable new-cap */
    checkIfIsOptional(field, value) ||
    checkIfParamDoesNotExist(field, "TDFieldOptionCreditCard") || {
      isValid: validateCard(unmasks.CreditCard(value)),
      message: settings.translations.field_invalid.replace(
        "%field_name%",
        title
      )
    }
  );
};

const validatePasswordConfirmationIfNeeded = ({
  field,
  value,
  fieldsArray,
  settings
}) => {
  const isConfirmation = field.name === "password_confirmation";
  return (
    checkIfIsOptional(field, value) || {
      isValid:
        !isConfirmation ||
        checkIfConfirmationMatch(fieldsArray, "password", value),
      message: isConfirmation
        ? settings.translations.passwords_do_not_match
        : null
    }
  );
};

export default {
  Required: {
    validate: props => validateRequiredIfNeeded(props)
  },
  PhoneNumber: {
    validate: props => validatePhoneNumberIfNeeded(props)
  },
  CountryPhoneNumber: {
    validate: props => validateCountryPhoneNumberIfNeeded(props)
  },
  Month: {
    validate: props =>
      validateIfNeeded(props, MONTH_PATTERN, FIELD_PARAMS.fieldOptionMonth)
  },
  ZipCode: {
    validate: props =>
      validateIfNeeded(props, ZIP_CODE_PATTERN, FIELD_PARAMS.fieldOptionZipCode)
  },
  Year: {
    validate: props =>
      validateIfNeeded(props, YEAR_PATTERN, FIELD_PARAMS.fieldOptionYear)
  },
  EmailAddress: {
    validate: props =>
      validateIfNeeded(props, EMAIL_PATTERN, FIELD_PARAMS.fieldOptionEmail)
  },
  ExpirationDate: {
    validate: props => validateExpirationDateIfNeeded(props)
  },
  RegExp: {
    validate: props => validateRegExpIfNeeded(props)
  },
  DateOfBirth: {
    validate: props => validateDOBIfNeeded(props)
  },
  CreditCard: {
    validate: props => validateCreditCardIfNeeded(props)
  },
  PasswordConfirmation: {
    validate: props => validatePasswordConfirmationIfNeeded(props)
  },
  ModernizedPhoneNumber: {
    validate: props => validateModernizedPhoneNumber(props)
  }
};
